import React, { useState } from "react";
import { Card, Col, Empty, Row, Select, Typography } from "antd";

import InfiniteActivityList from "@Components/activity/InfiniteActivityList";
import { ILoadNextPage } from "@Components/shared/types";

import { IActivityDetails } from "@Models/IActivityDetails";
import { IAssociatedStatuses } from "./hooks/useAssociatedStatuses";
import SessionManager from "@Core/session";

type IProps = {
    renderResults: boolean;
    activities: IActivityDetails[];
    associatedStatusesMap: Map<string, IAssociatedStatuses>;
    hasNextPage: boolean;
    isNextPageLoading: boolean;
    loadNextPage: ILoadNextPage;
    handleActivitySelect: (userId: string, activityInstanceId: string) => void;
};

const FILTER_OPTIONS = ["Your Activities", "Show all", "CME Incomplete", "Community Activities (CME)"] as const;
type IFilterOptions = (typeof FILTER_OPTIONS)[number];

const ActivitySearchList = ({
    renderResults,
    activities,
    associatedStatusesMap,
    hasNextPage,
    isNextPageLoading,
    loadNextPage,
    handleActivitySelect,
}: IProps) => {
    const [filter, setFilter] = useState<IFilterOptions>("Your Activities");

    const hasCME = associatedStatusesMap.size > 0;
    const { userId } = SessionManager.getServiceUser();
    const urlParamUserId = new URLSearchParams(location.search).get("user");
    const isLoggedInUserPage = urlParamUserId === userId;

    const { Title } = Typography;

    // filter for CME activities. ie if there is an associated status
    const cmeActivities = activities.filter((activity) => {
        return associatedStatusesMap.has(activity.activityInstanceId);
    });

    const hasCMEActivities = cmeActivities.length > 0;

    // filter for CME activities that are incomplete
    const cmeIncompleteActivities = cmeActivities.filter((activity) => {
        const status = associatedStatusesMap.get(activity.activityInstanceId)?.status;
        return status && status !== "completed";
    });

    const filteredActivities = () => {
        switch (filter) {
            // excludes cme public activities
            case "Your Activities":
                return activities.filter((activity) => {
                    return activity.userId === urlParamUserId;
                });

            // any cme activity that is not complete
            case "CME Incomplete":
                return cmeIncompleteActivities.filter((activity) => {
                    return activity.activityMeta?.isCMEPublic || activity.userId === userId;
                });

            // activities from others that are cme public
            case "Community Activities (CME)":
                return cmeActivities.filter((activity) => {
                    const status = associatedStatusesMap.get(activity.activityInstanceId)?.status;
                    return activity.userId !== userId && status;
                });

            default:
                // filter out the isCMEPublic activities where there are no associated questionnaires
                return activities.filter((activity) => {
                    const status = associatedStatusesMap.get(activity.activityInstanceId)?.status;
                    return (activity.userId !== userId && status) || activity.userId === userId;
                });
        }
    };

    return (
        <Card className="activities-search-list">
            <Row className="activities-header" style={{ marginBottom: "20px" }}>
                <Col span={8} style={{ paddingLeft: "5px", alignItems: "center", display: "flex" }}>
                    <Title level={5} style={{ width: "100%" }}>
                        Activities
                    </Title>
                </Col>

                {hasCME && hasCMEActivities && isLoggedInUserPage ? (
                    <Select
                        defaultValue={FILTER_OPTIONS[0]}
                        onChange={(e: IFilterOptions) => {
                            setFilter(e);
                        }}
                    >
                        {FILTER_OPTIONS.map((option) => (
                            <Select.Option key={option}>{option}</Select.Option>
                        ))}
                    </Select>
                ) : null}
            </Row>

            {renderResults ? (
                <InfiniteActivityList
                    activities={filteredActivities()}
                    associatedStatusesMap={associatedStatusesMap}
                    filter={filter}
                    hasNextPage={hasNextPage}
                    isNextPageLoading={isNextPageLoading}
                    loadNextPage={loadNextPage}
                    handleActivitySelect={handleActivitySelect}
                />
            ) : (
                <Empty description={"No results to display."} style={{ paddingBottom: "40px", marginTop: "20px" }} />
            )}
        </Card>
    );
};

export default ActivitySearchList;
